/**
 * @fileoverview Public entry point for the in-browser ligand minimizer.
 *
 * Exposes the `LigandMinimizer` class -- the single surface the rest of BMaps
 * calls. It wraps the receptor pre-filter (`ReceptorContext`), the synchronous
 * single-point evaluator, and the L-BFGS driver, and adds an optional Web
 * Worker offload path for `minimize()` so the UI thread stays responsive.
 *
 * The numeric path is identical on the main thread and in the worker: both call
 * the same `minimize()` driver. The worker only changes *where* it runs. When
 * a worker is unavailable (Node/mocha, or a build where the webpack worker
 * import is not wired up yet), `minimize()` transparently falls back to running
 * the driver on the calling thread, so tests and server-side use always work.
 * The native webpack-5 worker import
 * `new Worker(new URL('./worker.js', import.meta.url), { type: 'module' })`
 * relies on that fallback to stay correct in any build where it does not wire
 * up.
 *
 * Units (the engine contract): nm, kJ/mol, e, rad. Conversion to/from the
 * BMaps Angstrom / kcal-mol convention happens at the call site
 * (`localMinimize()`), not here.
 *
 * @see proto/src/chemistry/LocalMinimizer/types.js
 * @see proto/src/chemistry/LocalMinimizer/worker.js
 */

import { ReceptorContext } from './forces/receptor.js';
import { Evaluator } from './forces/evaluator.js';
import { minimize as runMinimize } from './minimizer/minimize.js';
import { minimizeWithOptionalWasm, _resetWasmEvaluatorCache } from './wasm/wasmBackend.js';

/**
 * Upper bound on how long a worker request may take before we settle the
 * caller's promise without it. A worker can construct successfully yet never
 * reply -- a throw in its own microtask, a structured-clone failure on the
 * response, a hung in-worker WASM compile -- and `worker.onerror` does not fire
 * for those, so without a timeout the promise (and the awaiting `localMinimize`
 * compound, or a `warmUp()` that is documented to "always settle") would hang
 * forever. Both bounds are far above any legitimate time (minimize p95 target
 * ~1.5s; WASM compile is sub-second), so they only fire on a genuinely stuck
 * worker. `.unref?.()` keeps a pending timer from holding a Node process open
 * (no-op in the browser, where setTimeout returns a number).
 */
const WORKER_MINIMIZE_TIMEOUT_MS = 30_000;
const WORKER_WARMUP_TIMEOUT_MS = 15_000;

/**
 * @typedef {import('./types.js').LigandInput} LigandInput
 * @typedef {import('./types.js').ReceptorAtoms} ReceptorAtoms
 * @typedef {import('./types.js').MinimizationOptions} MinimizationOptions
 * @typedef {import('./types.js').MinimizationResult} MinimizationResult
 */

/**
 * Default pocket pruning radius, nm. 1.5 nm == 15 A, the authoritative
 * bfd-server `defaultResidueCutoff`.
 * @type {number}
 */
const DEFAULT_POCKET_RADIUS = 1.5;

/**
 * Public ligand energy-minimizer.
 *
 * Lifecycle:
 *   1. (optional) `prepareReceptor(receptor, center, radius)` -- once per
 *      receptor; the candidate list is reused across all subsequent calls.
 *   2. `evaluate(ligandInput)` -- synchronous single-point energy + gradient.
 *   3. `minimize(ligandInput, options)` -- returns a `Promise<MinimizationResult>`;
 *      runs in a worker when available, else on the calling thread.
 */
export class LigandMinimizer {
    /**
     * @param {Object} [config]
     * @param {boolean} [config.useWorker=true]  Offload `minimize()` to a Web
     *   Worker when one can be constructed. When false (or when worker
     *   construction fails), `minimize()` runs on the calling thread.
     * @param {() => Worker} [config.createWorker]  Injected worker factory used
     *   instead of the dynamic `workerFactory.js` import. Lets tests drive the
     *   worker message protocol with a fake Worker (no real browser runtime).
     * @param {boolean} [config.useWasm=true]  Use the WebAssembly evaluator
     *   backend (perf path) when available; on by default, falls back
     *   transparently to the JS engine. Pass false to force the JS engine.
     * @param {number} [config.workerMinimizeTimeoutMs]  Override the per-request
     *   worker-minimize timeout (ms). Defaults to {@link WORKER_MINIMIZE_TIMEOUT_MS}.
     *   Exposed for testability so a stuck-worker test need not wait the full 30s.
     * @param {number} [config.warmupTimeoutMs]  Override the warmup timeout (ms).
     *   Defaults to {@link WORKER_WARMUP_TIMEOUT_MS}. Exposed for testability so a
     *   never-replying-warmup test need not wait the full 15s.
     */
    constructor(config = {}) {
        /**
         * Prepared receptor context, or null until `prepareReceptor` is called.
         * Receptor-free (vacuum) minimization is supported by leaving this null.
         * @type {ReceptorContext|null}
         */
        this.receptorContext = null;

        /** @type {boolean} */
        this.useWorker = config.useWorker !== false;

        /**
         * Use the WebAssembly evaluator backend for the energy/gradient hot
         * path. On by default -- the engine always uses WASM when it can and
         * falls back transparently to the JS engine when WASM cannot load (an
         * environment without WebAssembly, or a build that did not bundle the
         * `.wasm`). Pass `useWasm: false` to force the JS engine (used by tests /
         * for bit-identical reference runs); there is no user-facing toggle.
         * @type {boolean}
         */
        this.useWasm = config.useWasm !== false;

        /**
         * Optional injected worker factory `() => Worker`. When present it is
         * used in place of the dynamic `workerFactory.js` import (which relies
         * on `import.meta.url` and a real `Worker`), so the worker round-trip
         * is unit-testable with a fake Worker.
         * @type {(() => Worker)|null}
         */
        this._createWorker = config.createWorker || null;

        /**
         * Per-request worker-minimize timeout, ms. Defaults to the module
         * constant; overridable via config so tests can drive the timeout-fires
         * path without waiting the full default. Does not change default behavior.
         * @type {number}
         */
        this._workerMinimizeTimeoutMs = config.workerMinimizeTimeoutMs
            ?? WORKER_MINIMIZE_TIMEOUT_MS;

        /**
         * Warmup timeout, ms. Defaults to the module constant; overridable via
         * config so tests can drive the no-reply warmup path without waiting the
         * full default. Does not change default behavior.
         * @type {number}
         */
        this._warmupTimeoutMs = config.warmupTimeoutMs ?? WORKER_WARMUP_TIMEOUT_MS;

        /**
         * True when the receptor changed since it was last sent to the worker;
         * the next `minimize()` re-sends it, otherwise the worker reuses its
         * cached copy (avoids re-transferring the candidate set every call).
         * @type {boolean}
         */
        this._receptorDirty = false;

        /**
         * The lazily-created worker, or null if not started / unavailable.
         * @type {Worker|null}
         */
        this._worker = null;
        /**
         * The in-flight worker-initialization promise (dynamic import +
         * construction), or null before the first attempt.
         * @type {Promise<Worker|null>|null}
         */
        this._workerInit = null;
        /** @type {boolean} True once we've decided the worker cannot be used. */
        this._workerUnavailable = false;
        /** @type {number} Monotonic request id for the worker message protocol. */
        this._seq = 0;
        /**
         * In-flight worker requests keyed by sequence id.
         * @type {Map<number, {resolve: Function, reject: Function}>}
         */
        this._pending = new Map();
    }

    /**
     * Pre-filter a receptor down to the atoms within `pocketRadius` of
     * `pocketCenter` and store them for reuse across minimizations.
     *
     * Numerical-agreement contract. The evaluator sums the ligand<->receptor
     * interaction with no distance cutoff, to mirror mm-server's
     * `NonbondedForce::NoCutoff`. That means every retained candidate
     * contributes -- so this prune radius is the de-facto interaction boundary,
     * not a mere performance filter. For the local energy to match mm-server,
     * the caller (`localMinimize`) must hand this method the same receptor atom
     * set bfd-server prunes for mm-server, namely the residues within the
     * residue cutoff (1.5 nm) of the *ligand* (not the pocket centre).
     * Concretely: either (a) pass an already-residue-pruned `receptor` plus a
     * `pocketRadius` generous enough to retain all of it, or (b) ensure
     * `pocketRadius` reproduces bfd-server's selection. A center-based radius
     * that is too small silently drops atoms mm-server kept (energy comes out
     * low); too large includes atoms it dropped (energy comes out high).
     *
     * The prune radius is exactly `pocketRadius + cutoffBuffer` (no hidden
     * multiplier). Reusing the prepared context across ligands in the same
     * pocket is the intended fast path.
     *
     * @param {ReceptorAtoms} receptor   Receptor atoms (coords nm, charges e,
     *   sigma nm, epsilon kJ/mol). Should already be the mm-server-equivalent
     *   set per the contract above.
     * @param {ArrayLike<number>} pocketCenter   Pocket center [x,y,z] in nm.
     * @param {number} [pocketRadius=1.5]        Prune radius in nm (the retained
     *   set; must cover the mm-server-equivalent atoms per the contract above).
     * @param {number} [cutoffBuffer=0]          Extra retention margin in nm.
     * @returns {void}
     */
    prepareReceptor(
        receptor, pocketCenter, pocketRadius = DEFAULT_POCKET_RADIUS, cutoffBuffer = 0,
    ) {
        const ctx = new ReceptorContext();
        // Prune radius = pocketRadius + cutoffBuffer (pass cutoff=0 to the
        // context so its radius formula does not add a hidden term). Because
        // evaluate() runs NoCutoff, this radius alone determines which atoms
        // interact -- see the agreement contract in the doc comment above.
        ctx.prepareReceptor(receptor, pocketCenter, pocketRadius, 0, cutoffBuffer);
        this.receptorContext = ctx;

        // The receptor changed: the next minimize() must re-send it to the
        // worker (which otherwise reuses its cached candidate set).
        this._receptorDirty = true;
    }

    /**
     * Clear the prepared receptor, reverting to receptor-free (vacuum)
     * minimization. The companion to {@link prepareReceptor}: it maintains the
     * same `_receptorDirty` bookkeeping (the receptor set changed, so the next
     * `minimize()` must not let the worker reuse its stale cached candidate
     * set). Use instead of poking `receptorContext` directly.
     *
     * @returns {void}
     */
    clearReceptor() {
        this.receptorContext = null;
        this._receptorDirty = true;
    }

    /**
     * Synchronous single-point energy + gradient at the ligand's current
     * coordinates (or at `coordsOverride` if supplied). Runs on the calling
     * thread -- intended for validation, finite-difference checks, and
     * inexpensive UI probes.
     *
     * @param {LigandInput} ligandInput
     * @param {Float64Array} [coordsOverride]  Evaluate at these coords instead
     *   of `ligandInput.coords` (not retained / not mutated).
     * @returns {{ energy: number, gradient: Float64Array }}  Energy kJ/mol;
     *   gradient is a fresh copy (length 3*N, kJ/mol/nm) safe to retain.
     */
    evaluate(ligandInput, coordsOverride) {
        const evaluator = new Evaluator(ligandInput, this.receptorContext);
        const coords = coordsOverride || ligandInput.coords;
        const { energy, gradient } = evaluator.evaluate(coords);
        // Copy the gradient: the evaluator's buffer is reused on the next call.
        return { energy, gradient: Float64Array.from(gradient) };
    }

    /**
     * Minimize the ligand. Returns a Promise that resolves to a
     * `MinimizationResult`. Offloaded to a Web Worker when one is available,
     * otherwise run on the calling thread (the numeric path is identical).
     *
     * @param {LigandInput} ligandInput
     * @param {MinimizationOptions} [options]   The `onIteration` callback and
     *   any function-valued option cannot cross the worker boundary; when a
     *   worker is used those are ignored (a structured-clone-safe subset of
     *   options is forwarded). On the main-thread path all options apply.
     * @returns {Promise<MinimizationResult>}
     */
    minimize(ligandInput, options = {}) {
        if (this.useWorker && !this._workerUnavailable
            && (this._createWorker || typeof Worker !== 'undefined')) {
            return this._ensureWorker().then((worker) => {
                if (worker) return this._minimizeInWorker(worker, ligandInput, options);
                // Worker init failed (e.g. factory import / construction error):
                // fall back to the identical main-thread numeric path.
                return this._minimizeMainThread(ligandInput, options);
            });
        }
        // Main-thread fallback: identical numeric path, runs synchronously and
        // resolves the promise with the result. This is the path taken under
        // mocha/node (no `Worker` global) and when `useWorker` is false.
        return Promise.resolve(this._minimizeMainThread(ligandInput, options));
    }

    /**
     * Pre-warm the worker so the first real `minimize()` pays only compute, not
     * worker construction + the in-worker WASM compile/instantiate. Constructs
     * the worker (via the same lazy path `minimize()` uses) and sends a
     * lightweight `'warmup'` message that forces the worker to load its WASM
     * module. Best-effort and idempotent: `_ensureWorker()` reuses an existing
     * worker, so repeated calls cost at most one worker + one compile.
     *
     * Always settles and never rejects — resolves `false` when there is no
     * worker environment (Node/mocha, or `useWorker:false`), when worker
     * construction fails, or when the worker reports a WASM-load error (the
     * first real minimize then falls back to the JS engine, unchanged). Resolves
     * `true` once the worker acknowledges a successful warmup.
     *
     * @returns {Promise<boolean>}  true if the worker warmed up, else false.
     */
    warmUp() {
        if (!this.useWorker || this._workerUnavailable
            || !(this._createWorker || typeof Worker !== 'undefined')) {
            // No worker here: nothing to pre-warm. The main-thread fallback
            // compiles WASM lazily on first minimize, which is fine.
            return Promise.resolve(false);
        }
        return this._ensureWorker().then((worker) => {
            if (!worker) return false; // construction failed -> minimize() falls back
            const seq = ++this._seq;
            return new Promise((resolve) => {
                // Map the shared seq-keyed protocol onto a best-effort boolean:
                // the worker's ack resolves true; any failure -- or no reply within
                // the warmup timeout -- resolves false.
                const timer = setTimeout(() => {
                    if (this._pending.delete(seq)) resolve(false);
                }, this._warmupTimeoutMs);
                timer.unref?.();
                this._pending.set(seq, {
                    resolve: () => resolve(true),
                    reject: () => resolve(false),
                    timer,
                });
                try {
                    worker.postMessage({ type: 'warmup', seq, useWasm: this.useWasm });
                } catch {
                    clearTimeout(timer);
                    this._pending.delete(seq);
                    resolve(false);
                }
            });
        });
    }

    /**
     * Run the driver on the calling thread. Shared by the fallback path and the
     * worker's own handler (via `worker.js`).
     *
     * When `useWasm` is set, this runs the driver through the WASM evaluator
     * (numeric path otherwise identical), falling back transparently to the JS
     * engine if the module cannot load. The shared fallback logic lives in
     * `minimizeWithOptionalWasm` so the worker path uses the same implementation.
     *
     * @param {LigandInput} ligandInput
     * @param {MinimizationOptions} [options]
     * @returns {Promise<MinimizationResult>}
     */
    _minimizeMainThread(ligandInput, options = {}) {
        return minimizeWithOptionalWasm(
            runMinimize, ligandInput, this.receptorContext, options, this.useWasm,
        );
    }

    /**
     * Lazily construct the Web Worker. Resolves to the worker, or to null (and
     * latches `_workerUnavailable`) if construction is not possible in this
     * environment (e.g. Node/mocha, or a build where the worker import isn't
     * wired up).
     *
     * The actual `new Worker(new URL('./worker.js', import.meta.url), ...)`
     * lives in `workerFactory.js` and is reached only via dynamic `import()`.
     * `import.meta` is invalid under the CommonJS transform mocha uses, so
     * keeping it out of this statically-parsed module is what lets the tests
     * load `index.js` at all; the dynamic import is taken only when a real
     * `Worker` global exists.
     *
     * @returns {Promise<Worker|null>}
     */
    _ensureWorker() {
        if (this._worker) return Promise.resolve(this._worker);
        if (this._workerUnavailable) return Promise.resolve(null);
        if (this._workerInit) return this._workerInit;

        // Use the injected factory (tests) when present; otherwise the real
        // dynamic import of workerFactory.js (kept out of the static graph so
        // its `import.meta.url` never reaches the CJS/babel test transform).
        const factory = this._createWorker
            ? Promise.resolve({ createMinimizerWorker: this._createWorker })
            : import('./workerFactory.js');
        this._workerInit = factory
            .then(({ createMinimizerWorker }) => {
                const worker = createMinimizerWorker();
                worker.onmessage = (e) => this._onWorkerMessage(e);
                worker.onerror = () => {
                    // A worker that errors at construction/runtime: disable it and
                    // let subsequent calls use the main-thread path.
                    this._failAllPending(new Error('minimizer worker error'));
                    this._workerUnavailable = true;
                    this._worker = null;
                };
                this._worker = worker;
                return worker;
            })
            .catch(() => {
                // Factory import or worker construction failed -> permanent
                // main-thread fallback for this instance.
                this._workerUnavailable = true;
                return null;
            });
        return this._workerInit;
    }

    /**
     * Post a minimize request to the worker and return a Promise for its
     * result. Coordinate buffers are sent as transferables to avoid copying; a
     * copy of the starting coords is transferred so the caller's input buffer
     * is untouched.
     *
     * @param {Worker} worker
     * @param {LigandInput} ligandInput
     * @param {MinimizationOptions} options
     * @returns {Promise<MinimizationResult>}
     */
    _minimizeInWorker(worker, ligandInput, options) {
        const seq = ++this._seq;
        const hasReceptor = !!this.receptorContext;
        // Re-send the receptor only when it changed since the last handoff;
        // otherwise tell the worker to reuse its cached candidate set.
        const sendReceptor = hasReceptor && this._receptorDirty;
        return new Promise((resolve, reject) => {
            // Settle the caller if the worker never replies (it can construct yet
            // never post back, with no `onerror`); without this the promise -- and
            // the awaiting localMinimize compound -- would hang forever. The caller
            // (localMinimize) catches the rejection and fails just that compound.
            const timer = setTimeout(() => {
                if (this._pending.delete(seq)) {
                    reject(new Error(
                        `minimizer worker did not respond within ${this._workerMinimizeTimeoutMs}ms`,
                    ));
                }
            }, this._workerMinimizeTimeoutMs);
            timer.unref?.();
            this._pending.set(seq, { resolve, reject, timer });

            // Send a copy of coords so the transfer doesn't neuter the caller's
            // buffer. Strip non-cloneable (function) options.
            const coordsCopy = Float64Array.from(ligandInput.coords);
            const msg = {
                type: 'minimize',
                seq,
                ligand: { ...ligandInput, coords: coordsCopy },
                receptor: sendReceptor ? serializeReceptor(this.receptorContext) : null,
                reuseReceptor: hasReceptor && !sendReceptor,
                options: cloneableOptions(options),
                // The worker builds + runs the WASM evaluator itself (it can't be
                // structured-cloned); it falls back to JS if WASM won't load there.
                useWasm: this.useWasm,
            };
            try {
                worker.postMessage(msg, [coordsCopy.buffer]);
                // Receptor now cached in the worker; don't re-send until it changes.
                if (sendReceptor) this._receptorDirty = false;
            } catch (err) {
                clearTimeout(timer);
                this._pending.delete(seq);
                reject(err);
            }
        });
    }

    /**
     * Worker message handler: resolve/reject the matching pending request.
     * @param {MessageEvent} e
     * @returns {void}
     */
    _onWorkerMessage(e) {
        const data = e.data || {};
        const entry = this._pending.get(data.seq);
        if (!entry) return;
        this._pending.delete(data.seq);
        if (entry.timer) clearTimeout(entry.timer);
        if (data.type === 'result') {
            entry.resolve(data.result);
        } else {
            entry.reject(new Error(data.error || 'minimizer worker failure'));
        }
    }

    /**
     * Reject all in-flight requests (used when the worker dies).
     * @param {Error} err
     * @returns {void}
     */
    _failAllPending(err) {
        for (const entry of this._pending.values()) {
            if (entry.timer) clearTimeout(entry.timer);
            entry.reject(err);
        }
        this._pending.clear();
    }

    /**
     * Tear down the worker (if any). Idempotent.
     * @returns {void}
     */
    dispose() {
        if (this._worker) {
            this._worker.terminate();
            this._worker = null;
        }
        this._failAllPending(new Error('minimizer disposed'));
        // Release the module-scoped main-thread WASM evaluator cache. On the
        // worker path the worker owns its own cache (reclaimed by terminate()
        // above); on the in-process path (tests/SSR/no-worker) the cached
        // evaluator's malloc'd buffers would otherwise stay pinned after
        // teardown. The reset is idempotent (no-op when the cache is empty), so
        // it's safe even when this instance never touched the main-thread path.
        _resetWasmEvaluatorCache();
    }
}

/**
 * Flatten a prepared {@link ReceptorContext} into a structured-clone-safe
 * payload for the worker. The worker rebuilds a context from these buffers.
 *
 * @param {ReceptorContext} ctx
 * @returns {{
 *   coords: Float64Array, charges: Float64Array,
 *   sigma: Float64Array, epsilon: Float64Array
 * }}
 */
export function serializeReceptor(ctx) {
    return {
        coords: ctx.coords,
        charges: ctx.charges,
        sigma: ctx.sigma,
        epsilon: ctx.epsilon,
    };
}

/**
 * Strip non-structured-cloneable fields (functions) from minimization options
 * before sending them across the worker boundary. `onIteration` cannot cross
 * the boundary, so it is dropped for the worker path (documented on `minimize`).
 *
 * @param {MinimizationOptions} options
 * @returns {MinimizationOptions}
 */
export function cloneableOptions(options) {
    const out = {};
    for (const key of Object.keys(options)) {
        if (typeof options[key] === 'function') continue;
        out[key] = options[key];
    }
    return out;
}

export default LigandMinimizer;
